import * as React from 'react'
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell,
  Checkbox, Chip, Typography, Button, Pagination, TableSortLabel,
} from '@mui/material'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import {dataGridWrapSx, headerCellSx, cellSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, SUCCESS, WARNING, INFO} from '../../../../styles/foundation/colors'
import {SOURCE_LABEL} from './catalogueState'
import {packLabel} from './catalogueMath'
import {chipSx} from '../components/tone'
import {int, kg} from '../format'
import EmptyState from '../components/EmptyState'

// Raw colour per source (see components/tone.js): catalogue uploads are
// routine (MUTED/'neutral'), auto-added rows need attention (WARNING/'warn'),
// an OMS/WMS pull is informational (INFO/'info').
const SOURCE_TONE = {catalogue: 'neutral', auto: 'warn', oms: 'info'}

const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/**
 * Plain MUI Table, not DataGrid — see the task brief: multi-line composite
 * cells (SKU code + product + dq/mapping sub-lines), a page-scoped
 * select-all with an indeterminate state, and sort accessors that have to
 * read the ROLLED value (a mother's combined dead weight/shipments), not the
 * row's own field. Modelled on disputes-v2/DisputeGrid.js.
 *
 * `rows` is the already-filtered set (every matching row, every page) — this
 * component paginates it locally. `roll` is `rollUp(skus)` from
 * catalogueMath, materialised once by the caller so sorting never calls it
 * inside a comparator.
 */
const NUMERIC_SORT_KEYS = new Set(['dead', 'ships'])

export default function CatalogueTable({
  rows, roll, packs, page, perPage, onPageChange,
  selection, onToggleSelect, onSelectAllOnPage, onFixCode, onReview, onClearFilters,
}) {
  const [sort, setSort] = React.useState({key: null, dir: 'asc'})

  const handleSort = (key) => {
    setSort((prev) => (
      prev.key === key
        ? {key, dir: prev.dir === 'asc' ? 'desc' : 'asc'}
        : {key, dir: NUMERIC_SORT_KEYS.has(key) ? 'desc' : 'asc'}
    ))
  }

  // Decorate-sort-undecorate: the sort key is read out of `roll` exactly
  // once per row here, never inside the comparator itself.
  const sortedRows = React.useMemo(() => {
    if (!sort.key) return rows
    const decorated = rows.map((r) => {
      const rolled = roll[r.sku]
      const key = sort.key === 'sku'
        ? r.sku
        : sort.key === 'dead'
          ? (rolled ? rolled.dead : r.deadKg)
          : (rolled ? rolled.ships : r.ships)
      return {r, key}
    })
    decorated.sort((a, b) => {
      if (a.key < b.key) return sort.dir === 'asc' ? -1 : 1
      if (a.key > b.key) return sort.dir === 'asc' ? 1 : -1
      return 0
    })
    return decorated.map((d) => d.r)
  }, [rows, roll, sort])

  if (rows.length === 0) {
    return (
      <Box sx={dataGridWrapSx}>
        <EmptyState
          icon={SearchOffIcon}
          title='No SKUs match'
          body='Try a different search, or clear the source and sub-category filters.'
          action={<Button sx={secondaryBtnSx} onClick={onClearFilters}>Clear filters</Button>}
        />
      </Box>
    )
  }

  const pages = Math.max(1, Math.ceil(sortedRows.length / (perPage || 1)))
  const pageRows = sortedRows.slice((page - 1) * perPage, page * perPage)
  const pageCodes = pageRows.map((r) => r.sku)
  const allOnPage = pageCodes.length > 0 && pageCodes.every((c) => selection.has(c))
  const someOnPage = pageCodes.some((c) => selection.has(c))

  return (
    <Box sx={dataGridWrapSx}>
      <Table sx={{minWidth: 960}}>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx} padding='checkbox'>
              <Checkbox
                size='small'
                checked={allOnPage}
                indeterminate={someOnPage && !allOnPage}
                onChange={() => onSelectAllOnPage(pageCodes, !allOnPage)}
                inputProps={{'aria-label': 'Select all rows on this page'}}
              />
            </TableCell>
            <TableCell sx={headerCellSx}>
              <TableSortLabel
                active={sort.key === 'sku'}
                direction={sort.key === 'sku' ? sort.dir : 'asc'}
                onClick={() => handleSort('sku')}
              >
                SKU code
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellSx}>Source</TableCell>
            <TableCell sx={headerCellSx}>Category</TableCell>
            <TableCell sx={headerCellSx}>Sub-category</TableCell>
            <TableCell sx={{...headerCellSx, textAlign: 'right'}}>
              <TableSortLabel
                active={sort.key === 'dead'}
                direction={sort.key === 'dead' ? sort.dir : 'desc'}
                onClick={() => handleSort('dead')}
              >
                Dead weight
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellSx}>Default packing material</TableCell>
            <TableCell sx={{...headerCellSx, textAlign: 'right'}}>
              <TableSortLabel
                active={sort.key === 'ships'}
                direction={sort.key === 'ships' ? sort.dir : 'desc'}
                onClick={() => handleSort('ships')}
              >
                Shipments
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellSx}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pageRows.map((row) => {
            const rolled = roll[row.sku] || {rolled: false, kids: [], ships: row.ships, dead: row.deadKg, added: 0}
            const needsReview = row.source === 'auto' && !row.reviewed && !row.dq

            return (
              <TableRow key={row.sku} hover selected={selection.has(row.sku)}>
                <TableCell sx={cellSx} padding='checkbox'>
                  <Checkbox
                    size='small'
                    checked={selection.has(row.sku)}
                    onChange={() => onToggleSelect(row.sku)}
                    inputProps={{'aria-label': 'Select SKU ' + row.sku}}
                  />
                </TableCell>

                <TableCell sx={cellSx}>
                  <Typography sx={{fontFamily: 'monospace', fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, wordBreak: 'break-all'}}>
                    {row.sku}
                  </Typography>
                  <Typography
                    title={row.product}
                    sx={{...subtleTextSx, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                  >
                    {row.product}
                  </Typography>
                  {row.dq && <Chip size='small' label='Unusable product code' sx={{...chipSx('danger'), mt: 0.5}} />}
                  {row.childOf && (
                    <Typography sx={{...subtleTextSx, mt: 0.25}}>↳ Mapped to {row.childOf}</Typography>
                  )}
                  {rolled.rolled && (
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: INFO, mt: 0.25}}>
                      Mother of {int(rolled.kids.length)} codes
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={cellSx}>
                  <Chip size='small' label={SOURCE_LABEL[row.source] || row.source} sx={chipSx(SOURCE_TONE[row.source] || 'neutral')} />
                  {needsReview && (
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: WARNING, mt: 0.25}}>Needs review</Typography>
                  )}
                </TableCell>

                <TableCell sx={cellSx}>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{row.cat}</Typography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{row.sub}</Typography>
                </TableCell>

                <TableCell sx={{...cellSx, textAlign: 'right'}}>
                  {rolled.rolled && rolled.dead !== row.deadKg ? (
                    <>
                      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>
                        {kg(rolled.dead)}
                      </Typography>
                      <Typography sx={subtleTextSx}>own {kg(row.deadKg)}</Typography>
                    </>
                  ) : (
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: row.childOf ? SUBTLE : NAVY}}>
                      {kg(row.deadKg)}
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={cellSx}>
                  <Typography sx={subtleTextSx}>{packLabel(packs, row.packId)}</Typography>
                </TableCell>

                <TableCell sx={{...cellSx, textAlign: 'right'}}>
                  <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}}>
                    {int(rolled.ships)}
                  </Typography>
                  {rolled.rolled && rolled.added > 0 && (
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUCCESS}}>
                      +{int(rolled.added)} rolled up
                    </Typography>
                  )}
                  {rolled.ships === 0 && <Typography sx={subtleTextSx}>Not shipped yet</Typography>}
                </TableCell>

                <TableCell sx={cellSx}>
                  {row.dq && <Button size='small' sx={secondaryBtnSx} onClick={() => onFixCode(row)}>Fix code</Button>}
                  {!row.dq && needsReview && (
                    <Button size='small' sx={secondaryBtnSx} onClick={() => onReview(row)}>Review</Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {pages > 1 && (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', p: 1.5}}>
          <Pagination
            size='small'
            page={page}
            count={pages}
            onChange={(e, p) => onPageChange(p)}
            sx={{'& .MuiPaginationItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}}
          />
        </Box>
      )}
    </Box>
  )
}
